import axios, { AxiosError, AxiosInstance } from 'axios';
import {
  ApiError,
  MaintenanceError,
  NetworkError,
  RequestTimeoutError,
} from '@/core/errors/errors';
import type {
  GameItem,
  MaintenanceStatus,
  MlInfo,
  MlDiamondBonus,
  PaymentChannel,
  ProductItem,
  ProductCategory,
  PurchaseResult,
  PurchaseHistoryItem,
  TopupHistoryItem,
  UserProfile,
  UserNotification,
  SpinInfo,
  SpinResult,
  DeviceInfo,
  DeviceListResponse,
  LeaderboardEntry,
  PackProvider,
  PackCategory,
  OoredooParentCategory,
  OoredooCollectionItem,
  PackOrder,
  PackOrderRequest,
  EventData,
  UserGuidePostDetail,
  UserGuidePostSummary,
  VoucherCategory,
  VoucherProduct,
  VoucherOrder,
} from '@/core/models/types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://0x7f.aurax.sbs';

function normalizeOoredooPhone(value: string): string {
  let digits = String(value || '').replace(/\D/g, '');
  if (digits.startsWith('00')) digits = digits.slice(2);
  if (digits.startsWith('95')) digits = digits.slice(2);
  if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

// Device detection helper
function getDeviceInfo(): { deviceId: string; deviceName: string } {
  const userAgent = navigator.userAgent;
  let deviceName = 'Unknown Device';
  
  // Try to detect device name from user agent
  if (userAgent.match(/iPhone/i)) {
    deviceName = 'iPhone';
  } else if (userAgent.match(/iPad/i)) {
    deviceName = 'iPad';
  } else if (userAgent.match(/Android/i)) {
    const match = userAgent.match(/Android.*?;\s*([^)]+)\)/);
    deviceName = match ? match[1].trim() : 'Android Device';
  } else if (userAgent.match(/Windows/i)) {
    deviceName = 'Windows PC';
  } else if (userAgent.match(/Mac/i)) {
    deviceName = 'Mac';
  } else if (userAgent.match(/Linux/i)) {
    deviceName = 'Linux';
  }
  
  // Generate or retrieve device ID
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem('device_id', deviceId);
  }
  
  return { deviceId, deviceName };
}

class ApiClient {
  private readonly baseUrl = API_BASE_URL;
  private readonly http: AxiosInstance;
  private token: string | null = null;

  constructor() {
    this.http = axios.create({ baseURL: this.baseUrl, timeout: 20000 });
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }

  setToken(token: string): void {
    this.token = token;
  }

  clearToken(): void {
    this.token = null;
  }

  private headers(auth = false): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (auth && this.token) headers.Authorization = `Bearer ${this.token}`;
    return headers;
  }

  private firstNonEmpty(...values: unknown[]): string {
    for (const value of values) {
      const text = value == null ? '' : String(value).trim();
      if (text) return text;
    }
    return '';
  }

  private idValue(value: unknown): number | string {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const text = value == null ? '' : String(value).trim();
    if (!text) return 0;
    return /^\d+$/.test(text) ? Number(text) : text;
  }

  private stringRecord(value: unknown): Record<string, string> {
    if (typeof value !== 'object' || !value || Array.isArray(value)) return {};
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [key, String(item ?? '')]),
    );
  }

  private productTypeFromName(name: string): string {
    const normalized = name.replace(/\s+/g, ' ').trim();
    if (/^\d[\d\s,._]*$/.test(normalized)) return 'Items';
    const withoutLeadingAmount = normalized
      .replace(/^\d[\d\s,._]*(?:\+\d[\d\s,._]*)*\s*/i, '')
      .trim();
    const withoutBonusAmount = withoutLeadingAmount
      .replace(/\s*\+\s*\d[\d\s,._]*\s+.+$/i, '')
      .trim();
    const withoutTrailingQuantity = withoutBonusAmount
      .replace(/\s*(?:[*x×]\s*\d+|\d+\s*(?:pcs?|pieces?))\s*$/i, '')
      .replace(/\s+/g, ' ')
      .trim();
    return withoutTrailingQuantity && withoutTrailingQuantity !== normalized ? withoutTrailingQuantity : '';
  }

  private voucherProductFromApi(value: Record<string, unknown>): VoucherProduct {
    return {
      id: this.idValue(value.id),
      title: String(value.title ?? ''),
      description: String(value.description ?? ''),
      categoryId: this.idValue(value.category_id),
      categoryTitle: String(value.category_title ?? ''),
      unitPrice: Number(value.unit_price ?? 0),
      faceValue: value.face_value == null ? null : Number(value.face_value),
      imageUrl: String(value.image_url ?? ''),
      stock: Number(value.stock ?? 0),
      currency: String(value.currency ?? 'MMK'),
    };
  }

  private voucherOrderFromApi(value: Record<string, unknown>): VoucherOrder {
    return {
      id: Number(value.id),
      providerOrderId: value.order_id == null ? null : Number(value.order_id),
      transactionId: value.transaction_id == null ? null : Number(value.transaction_id),
      productId: this.idValue(value.product_id),
      productTitle: String(value.product_title ?? ''),
      quantity: Number(value.quantity ?? 0),
      unitPrice: Number(value.unit_price ?? 0),
      totalPrice: Number(value.total_price ?? 0),
      status: String(value.status ?? ''),
      deliveryItems: Array.isArray(value.delivery_items) ? value.delivery_items.map(String) : [],
      refunded: value.refunded === true,
      message: String(value.message ?? ''),
      createdAt: String(value.created_at ?? ''),
      updatedAt: String(value.updated_at ?? ''),
      pollUrl: String(value.poll_url ?? ''),
    };
  }

  private categoryKeyFromName(name: string): string {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'items';
  }

  private gameFromApi(e: Record<string, unknown>): GameItem {
    const notice = this.firstNonEmpty(
      e.notes,
      e.note,
      e.notice,
      e.warning,
      e.description,
      e.instructions,
      e.remark,
      e.remarks,
    );
    return {
      id: this.idValue(e.id ?? e.product_slug ?? e.slug),
      game: this.firstNonEmpty(e.game, e.name),
      slug: e.slug == null && e.product_slug == null ? null : String(e.slug ?? e.product_slug),
      logo: String(e.logo ?? ''),
      banner: String(e.banner ?? e.url ?? e.logo ?? ''),
      message: String(e.message ?? ''),
      notes: notice || null,
      notice: notice || null,
      fields: Array.isArray(e.fields) ? e.fields.map(String) : null,
      count: e.count == null ? null : Number(e.count),
      isDirectTopup: e.is_direct_topup === true,
      isGame: e.is_game !== false,
      is_event: e.is_event === true,
      is_user_guide: e.is_user_guide === true,
      post_count: e.post_count == null ? undefined : Number(e.post_count),
      serversRequired: e.servers_required === true,
      servers: this.stringRecord(e.servers),
    };
  }

  private splitItemsByNameType(category: ProductCategory): ProductCategory[] {
    const groups = new Map<string, ProductItem[]>();
    const ungrouped: ProductItem[] = [];

    for (const item of category.items) {
      const type = this.productTypeFromName(item.name);
      if (!type) {
        ungrouped.push(item);
        continue;
      }
      groups.set(type, [...(groups.get(type) ?? []), item]);
    }

    const result: ProductCategory[] = [];
    for (const [type, items] of groups) {
      if (items.length > 1) {
        result.push({
          ...category,
          id: `${category.id}:${this.categoryKeyFromName(type)}`,
          categoryKey: `${category.categoryKey}-${this.categoryKeyFromName(type)}`,
          displayName: type,
          items,
        });
      } else {
        ungrouped.push(items[0]);
      }
    }

    if (ungrouped.length) {
      result.push({
        ...category,
        id: `${category.id}:ingameitem`,
        categoryKey: `${category.categoryKey}-ingameitem`,
        displayName: 'InGame Packages',
        items: ungrouped,
      });
    }

    if (result.length === 1) {
      return [{
        ...result[0],
        id: `${category.id}:items`,
        categoryKey: `${category.categoryKey}-items`,
        displayName: 'Items',
      }];
    }

    return result.length > 1 ? result : [category];
  }

  private productFromApi(
    e: Record<string, unknown>,
    defaults: { productSlug?: string; confirmationMode?: string; rolecheckEnabled?: boolean } = {},
  ): ProductItem {
    const productSlug = this.firstNonEmpty(e.product_slug, e.game_code, defaults.productSlug);
    const productCode = this.firstNonEmpty(e.productcode, e.product_code, e.denom_id, e.item_key, e.code);
    const source = this.firstNonEmpty(e.source);
    const isAuto = source !== 'manual' && (source === 'auto' || source === 'g2bulk' || Boolean(productSlug && productCode));
    const rolecheckEnabled =
      e.rolecheck_enabled == null && e.rolecheckEnabled == null
        ? defaults.rolecheckEnabled ?? (isAuto && defaults.confirmationMode === 'CHECK_AND_ORDER')
        : e.rolecheck_enabled == null
          ? Boolean(e.rolecheckEnabled)
          : Boolean(e.rolecheck_enabled);
    const eta = (typeof e.eta === 'object' && e.eta ? e.eta : null) as Record<string, unknown> | null;

    return {
      id: e.id == null
        ? this.idValue(isAuto && productSlug && productCode ? `${productSlug}:${productCode}` : productCode)
        : this.idValue(e.id),
      source,
      name: this.firstNonEmpty(e.name, e.display_name, e.package_name),
      price: String(e.price ?? e.reseller_price ?? ''),
      originalPrice: e.original_price == null ? null : String(e.original_price),
      currency: String(e.currency ?? 'MMK'),
      code: this.firstNonEmpty(e.code, e.product_code),
      productSlug,
      productCode,
      gameId: Number(e.game_id ?? 0),
      image: String(e.icon_url ?? e.url ?? e.image_url ?? ''),
      input: String(e.input ?? '2'),
      country: e.country == null ? null : String(e.country),
      isDirectTopup: e.is_direct_topup == null ? isAuto : Boolean(e.is_direct_topup),
      pid: String(e.pid ?? ''),
      categoryId: Number(e.category_id ?? 0),
      isGolang: Boolean(e.is_golang),
      rolecheckEnabled,
      isOtp: Boolean(e.is_otp),
      eta: eta
        ? {
            label: String(eta.label ?? ''),
            display: String(eta.display ?? ''),
            medianSeconds: eta.median_seconds == null ? null : Number(eta.median_seconds),
          }
        : null,
    };
  }

  private messageFromBody(body: unknown): string {
    if (typeof body === 'object' && body) {
      const payload = body as {
        message?: unknown;
        status_desc?: unknown;
        error?: { message?: unknown; status_desc?: unknown } | unknown;
        details?: { message?: unknown; status_desc?: unknown } | unknown;
      };
      const nestedError = payload.error && typeof payload.error === 'object'
        ? payload.error as { message?: unknown; status_desc?: unknown }
        : null;
      const nestedDetails = payload.details && typeof payload.details === 'object'
        ? payload.details as { message?: unknown; status_desc?: unknown }
        : null;
      const message = this.firstNonEmpty(
        payload.message,
        payload.status_desc,
        nestedError?.message,
        nestedError?.status_desc,
        nestedDetails?.message,
        nestedDetails?.status_desc,
      );
      if (message) return message;
    }
    if (typeof body === 'string') {
      const trimmed = body.trim();
      if (!trimmed) return 'Something went wrong';
      const lower = trimmed.toLowerCase();
      if (lower.startsWith('<!doctype') || lower.startsWith('<html') || lower.startsWith('<')) {
        return 'Server error. Please try again later.';
      }
      return trimmed.length > 300 ? trimmed.slice(0, 300) : trimmed;
    }
    return 'Something went wrong';
  }

  private maintenanceFromBody(body: unknown): MaintenanceError {
    if (typeof body === 'object' && body && 'maintenance' in body && (body as { maintenance?: unknown }).maintenance === true) {
      const msg = String((body as { message?: unknown }).message ?? '').trim();
      return new MaintenanceError(msg || 'Service is under maintenance. Please try again later.');
    }
    return new MaintenanceError('Service is under maintenance. Please try again later.');
  }

  private normalizeAxiosError(error: AxiosError): never {
    if (error.code === 'ECONNABORTED') {
      throw new RequestTimeoutError();
    }
    if (!error.response) {
      throw new NetworkError();
    }
    if (error.response.status === 503) {
      throw this.maintenanceFromBody(error.response.data);
    }
    throw new ApiError(this.messageFromBody(error.response.data));
  }

  private async safe<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const requestHeaders = error.config?.headers as Record<string, unknown> | undefined;
        const sentAuthenticatedRequest = Boolean(
          this.token && (requestHeaders?.Authorization || requestHeaders?.authorization),
        );
        if (status === 401 && sentAuthenticatedRequest) {
          this.clearToken();
          window.dispatchEvent(new CustomEvent('amt-auth-invalid'));
        }
        this.normalizeAxiosError(error);
      }
      throw error;
    }
  }

  async register(email: string, password: string, name: string): Promise<string> {
    return this.safe(async () => {
      const deviceInfo = getDeviceInfo();
      const res = await this.http.post('/api/auth/register', { 
        email,
        password, 
        name,
        device_id: deviceInfo.deviceId,
        device_name: deviceInfo.deviceName,
      }, { headers: this.headers() });
      if (res.status !== 201) throw new ApiError(this.messageFromBody(res.data));
      const token = (res.data as { token?: string }).token;
      if (token) return token;
      return this.login(email, password);
    });
  }

  async login(email: string, password: string): Promise<string> {
    return this.safe(async () => {
      const deviceInfo = getDeviceInfo();
      const res = await this.http.post('/api/auth/login', { 
        email,
        password,
        device_id: deviceInfo.deviceId,
        device_name: deviceInfo.deviceName,
      }, { headers: this.headers() });
      if (res.status !== 200) throw new ApiError(this.messageFromBody(res.data));
      const token = (res.data as { token?: string }).token;
      if (!token) throw new ApiError('Invalid credentials');
      return token;
    });
  }

  async fetchResetPasswordConfig(): Promise<{ turnstileSiteKey: string }> {
    return this.safe(async () => {
      const res = await this.http.get('/api/auth/reset-password/config', { headers: this.headers() });
      const data = res.data as Record<string, unknown>;
      return {
        turnstileSiteKey: String(data.turnstileSiteKey ?? ''),
      };
    });
  }

  async forgotPassword(email: string, turnstileToken: string): Promise<string> {
    return this.safe(async () => {
      const res = await this.http.post(
        '/api/auth/forgot-password',
        { email, turnstileToken },
        { headers: this.headers() },
      );
      return String(
        (res.data as { message?: unknown }).message ??
          'If that Gmail address exists, a password reset link has been sent.',
      );
    });
  }

  async fetchUser(): Promise<UserProfile> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user', { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      return {
        name: String(data.name ?? ''),
        coins: Number(data.coins ?? 0) || 0,
        email: data.email == null ? null : String(data.email),
      };
    });
  }

  async fetchSlider(): Promise<string[]> {
    return this.safe(async () => {
      const res = await this.http.get('/slider');
      return Array.isArray(res.data) ? res.data.map(String) : [];
    });
  }

  async fetchWelcomeMessage(): Promise<{ image_url: string; message: string; is_active: boolean }> {
    return this.safe(async () => {
      const res = await this.http.get('/welcome-message');
      const data = res.data as Record<string, unknown>;
      return {
        image_url: String(data.image_url ?? ''),
        message: String(data.message ?? ''),
        is_active: data.is_active === true,
      };
    });
  }

  async fetchGames(): Promise<GameItem[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/games');
      const games = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.games)
          ? res.data.games
          : [];
      return games.map((e: Record<string, unknown>) => this.gameFromApi(e));
    });
  }

  async fetchProductCatalog(game: string): Promise<{ game: GameItem | null; categories: ProductCategory[] }> {
    return this.safe(async () => {
      const res = await this.http.get(`/api/games/${encodeURIComponent(game)}/products`);
      const categories = res.data?.categories;
      const items = res.data?.items;
      const gameInfo = (typeof res.data?.game === 'object' && res.data.game ? res.data.game : {}) as Record<string, unknown>;
      const normalizedGame = Object.keys(gameInfo).length ? this.gameFromApi(gameInfo) : null;
      const gameFields = Array.isArray(gameInfo.fields) ? gameInfo.fields.map(String) : [];
      const productSlug = this.firstNonEmpty(gameInfo.product_slug, gameInfo.slug);
      const confirmationMode = this.firstNonEmpty(gameInfo.confirmation_mode);
      const gameRolecheckEnabled = gameInfo.rolecheck_enabled === true || gameInfo.rolecheckEnabled === true;
      const serversRequired = gameInfo.servers_required === true;
      const servers = this.stringRecord(gameInfo.servers);

      if (Array.isArray(items)) {
        return {
          game: normalizedGame,
          categories: this.splitItemsByNameType(
            {
              id: this.firstNonEmpty(productSlug, game),
              categoryKey: 'all',
              displayName: String(gameInfo.name ?? 'Packages'),
              sortOrder: 0,
              gameFormat: gameFields,
              serversRequired,
              servers,
              items: items
                .map((item) => this.productFromApi(item as Record<string, unknown>, {
                  productSlug,
                  confirmationMode,
                  rolecheckEnabled: gameRolecheckEnabled,
                }))
                .filter((item) => item.name && item.price),
            },
          ),
        };
      }

      if (!Array.isArray(categories)) return { game: normalizedGame, categories: [] };
      
      return {
        game: normalizedGame,
        categories: categories.flatMap((cat: Record<string, unknown>) =>
          this.splitItemsByNameType({
            id: this.idValue(cat.id),
            categoryKey: String(cat.category_key ?? ''),
            displayName: String(cat.display_name ?? ''),
            sortOrder: Number(cat.sort_order ?? 0),
            gameFormat: Array.isArray(cat.game_format) ? cat.game_format.map(String) : gameFields,
            serversRequired,
            servers,
            items: Array.isArray(cat.items)
              ? cat.items.map((e) => this.productFromApi(e as Record<string, unknown>, {
                  productSlug,
                  confirmationMode,
                  rolecheckEnabled: gameRolecheckEnabled,
                }))
              : [],
          }),
        ),
      };
    });
  }

  async fetchProducts(game: string): Promise<ProductCategory[]> {
    const catalog = await this.fetchProductCatalog(game);
    return catalog.categories;
  }

  async fetchVoucherCategories(): Promise<VoucherCategory[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/vouchers/categories');
      const categories = Array.isArray(res.data?.categories) ? res.data.categories : [];
      return categories.map((value: Record<string, unknown>) => ({
        id: this.idValue(value.id),
        title: String(value.title ?? ''),
        description: String(value.description ?? ''),
        imageUrl: String(value.image_url ?? ''),
        productCount: Number(value.product_count ?? 0),
        sort_order: Number(value.sort_order ?? 1000),
      }));
    });
  }

  async fetchVoucherProducts(categoryId?: number | string): Promise<VoucherProduct[]> {
    return this.safe(async () => {
      const path = categoryId == null
        ? '/api/vouchers/products'
        : `/api/vouchers/categories/${encodeURIComponent(String(categoryId))}/products`;
      const res = await this.http.get(path);
      const products = Array.isArray(res.data?.products) ? res.data.products : [];
      return products.map((value: Record<string, unknown>) => this.voucherProductFromApi(value));
    });
  }

  async fetchVoucherProduct(productId: number | string): Promise<VoucherProduct> {
    return this.safe(async () => {
      const res = await this.http.get(`/api/vouchers/products/${encodeURIComponent(String(productId))}`);
      return this.voucherProductFromApi((res.data?.product ?? {}) as Record<string, unknown>);
    });
  }

  async purchaseVoucher(productId: number | string, quantity: number): Promise<VoucherOrder> {
    return this.safe(async () => {
      const idempotencyKey = typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
            const random = Math.floor(Math.random() * 16);
            return (char === 'x' ? random : ((random & 0x3) | 0x8)).toString(16);
          });
      const res = await this.http.post(
        `/api/vouchers/products/${encodeURIComponent(String(productId))}/purchase`,
        { quantity },
        { headers: { ...this.headers(true), 'X-Idempotency-Key': idempotencyKey } },
      );
      return this.voucherOrderFromApi(res.data as Record<string, unknown>);
    });
  }

  async fetchVoucherOrders(): Promise<VoucherOrder[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/vouchers/orders?limit=50', { headers: this.headers(true) });
      const orders = Array.isArray(res.data?.orders) ? res.data.orders : [];
      return orders.map((value: Record<string, unknown>) => this.voucherOrderFromApi(value));
    });
  }

  async roleCheck(input: {
    productId: ProductItem['id'];
    productSlug?: string;
    productCode?: string;
    userId: string;
    zoneId?: string;
    fields?: Record<string, string>;
  }): Promise<{ key: string; player: string } | null> {
    return this.safe(async () => {
      const fields = input.fields ?? {};
      // Keys consumed into the canonical user_id / server_id below. Any other
      // declared field (e.g. charname) must still be forwarded to the API.
      const consumedKeys = new Set([
        'player_id', 'user_id', 'userid', 'account_id', 'gameid', 'id',
        'server_id', 'serverid', 'additional_id', 'zoneid', 'server',
      ]);
      const payload: Record<string, string> = {};
      for (const [key, value] of Object.entries(fields)) {
        const text = String(value ?? '').trim();
        if (text && !consumedKeys.has(key)) payload[key] = text;
      }
      payload.game = this.firstNonEmpty(input.productSlug, input.productId);
      payload.user_id = this.firstNonEmpty(fields.user_id, fields.userid, fields.account_id, fields.gameid, fields.id, input.userId);
      payload.server_id = this.firstNonEmpty(fields.server_id, fields.serverid, fields.additional_id, fields.zoneid, fields.server, input.zoneId);
      const res = await this.http.post('/api/games/checkPlayerId', payload, { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      if (data.valid !== true) return null;
      const player = String(data.name ?? '').trim();
      if (!player) return null;
      return { key: '', player };
    });
  }

  async purchase(payload: {
    productId: ProductItem['id'];
    userId: string;
    zoneId?: string;
    fields?: Record<string, string>;
    key?: string;
  }): Promise<PurchaseResult> {
    return this.safe(async () => {
      const fields = payload.fields ?? {};
      // Keys consumed into the canonical player_id / server_id below. Any other
      // declared field (e.g. charname) must still be forwarded to the API.
      const consumedKeys = new Set([
        'player_id', 'user_id', 'userid', 'account_id', 'gameid', 'id',
        'server_id', 'serverid', 'additional_id', 'zoneid', 'server',
      ]);
      const body: Record<string, string | number> = {};
      for (const [key, value] of Object.entries(fields)) {
        const text = String(value ?? '').trim();
        if (text && !consumedKeys.has(key)) body[key] = text;
      }
      body.product_id = payload.productId;
      body.player_id = this.firstNonEmpty(fields.player_id, fields.user_id, fields.userid, fields.account_id, fields.gameid, fields.id, payload.userId);
      const serverId = this.firstNonEmpty(fields.server_id, fields.serverid, fields.additional_id, fields.zoneid, fields.server, payload.zoneId);
      if (serverId) body.server_id = serverId;
      if (payload.key?.trim()) body.key = payload.key.trim();
      const res = await this.http.post(
        '/api/pay',
        body,
        { headers: this.headers(true) },
      );
      const data = res.data as Record<string, unknown>;
      return {
        status: data.status === true,
        message: String(data.message ?? 'Request sent'),
        playerId: data.player_id == null ? null : String(data.player_id),
        player: data.player_name == null && data.player == null ? null : String(data.player_name ?? data.player),
        region: data.region == null ? null : String(data.region),
        item: data.item == null ? null : String(data.item),
        orderId: data.provider_order_id == null && data.order_id == null ? null : String(data.provider_order_id ?? data.order_id),
        key: data.key == null ? null : String(data.key),
        order_status: data.order_status == null ? null : String(data.order_status) as PurchaseResult['order_status'],
      };
    });
  }

  async purchaseManual(payload: { productId: ProductItem['id']; fields: Record<string, string> }): Promise<PurchaseResult> {
    return this.safe(async () => {
      const normalizedUserId = this.firstNonEmpty(payload.fields.userid, payload.fields.gameid, payload.fields.id);
      const normalizedZoneId = this.firstNonEmpty(payload.fields.zoneid);
      const res = await this.http.post(
        '/api/pay',
        {
          product_id: payload.productId,
          game: this.firstNonEmpty(payload.fields.game, payload.fields.product_slug),
          userid: normalizedUserId,
          zoneid: normalizedZoneId,
          ...payload.fields,
        },
        { headers: this.headers(true) },
      );
      const data = res.data as Record<string, unknown>;
      return {
        status: data.status === true,
        message: String(data.message ?? 'Request sent'),
        playerId: data.player_id == null ? null : String(data.player_id),
        player: data.player == null ? null : String(data.player),
        region: data.region == null ? null : String(data.region),
        item: data.item == null ? null : String(data.item),
        orderId: data.provider_order_id == null && data.order_id == null ? null : String(data.provider_order_id ?? data.order_id),
        key: data.key == null ? null : String(data.key),
        order_status: data.order_status == null ? null : String(data.order_status) as PurchaseResult['order_status'],
      };
    });
  }

  async checkAccount(game: string, fields: Record<string, string>): Promise<string | null> {
    return this.safe(async () => {
      const res = await this.http.post(`/api/games/${encodeURIComponent(game)}/check-account`, fields, {
        headers: this.headers(true),
      });
      const data = res.data as { data?: Record<string, unknown> };
      const payload = data.data;
      if (!payload) return null;
      return String(payload.account_name ?? payload.username ?? payload.name ?? '') || null;
    });
  }

  async fetchTopupHistory(): Promise<TopupHistoryItem[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/payment/history', { headers: this.headers(true) });
      const history = ((res.data as { history?: unknown[] }).history ?? []) as Record<string, unknown>[];
      return history.map((e) => ({
        status: String(e.status ?? ''),
        createdAt: String(e.created_at ?? ''),
        method: String(e.method ?? ''),
        game: e.game == null ? null : String(e.game),
        item: e.item == null ? null : String(e.item),
        gameId: e.gameid == null ? null : String(e.gameid),
        zoneId: e.zoneid == null ? null : String(e.zoneid),
        playerName: e.player_name == null ? null : String(e.player_name),
        region: e.region == null ? null : String(e.region),
        productCode: e.product_code == null ? null : String(e.product_code),
        providerOrderId: e.provider_order_id == null ? null : String(e.provider_order_id),
        providerKey: e.provider_key == null ? null : String(e.provider_key),
        amount: String(e.amount ?? ''),
        reject_reason: e.message == null ? null : String(e.message),
      }));
    });
  }

  async verifyMsisdn(msisdn: string): Promise<boolean> {
    return this.safe(async () => {
      const res = await this.http.get(`/api/payment/verify?msisdn=${encodeURIComponent(msisdn)}`);
      if (res.status !== 200) return false;
      if (typeof res.data === 'object' && res.data && 'exists' in res.data) {
        return (res.data as { exists?: unknown }).exists === true;
      }
      return true;
    });
  }

  async fetchPaymentChannels(): Promise<PaymentChannel[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/payment/pay', { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      const result: PaymentChannel[] = [];
      if (Array.isArray(data.accounts)) {
        data.accounts.forEach((raw) => {
          const e = raw as Record<string, unknown>;
          const method = String(e.method ?? '');
          const value = String(e.account ?? '');
          if (method && value) {
            result.push({
              method,
              label: String(e.name ?? method.toUpperCase()),
              value,
              image_url: e.image_url ? String(e.image_url) : null,
            });
          }
        });
      }
      if (!result.length) {
        const kpay = data.kpay == null ? '' : String(data.kpay);
        const wave = data.wave == null ? '' : String(data.wave);
        if (kpay) result.push({ method: 'kpay', label: 'KPay', value: kpay, image_url: null });
        if (wave) result.push({ method: 'wave', label: 'Wave', value: wave, image_url: null });
      }
      return result;
    });
  }

  async submitTopup(msisdn: string, method: string, amount: string, screenshot: File): Promise<string> {
    return this.safe(async () => {
      const formData = new FormData();
      formData.set('msisdn', msisdn);
      formData.set('method', method);
      formData.set('amount', amount);
      formData.set('screenshot', screenshot);
      const res = await this.http.post('/api/payment/pay/public', formData, {
        headers: this.token ? { Authorization: `Bearer ${this.token}` } : undefined,
      });
      const data = res.data as Record<string, unknown>;
      return String(data.message ?? 'Top up submitted');
    });
  }

  async fetchPurchaseHistory(): Promise<PurchaseHistoryItem[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/pay/topuphistory', { headers: this.headers(true) });
      const history = ((res.data as { history?: unknown[] }).history ?? []) as Record<string, unknown>[];
      return history.map((e) => ({
        orderKind: e.order_kind == null ? null : String(e.order_kind),
        status: String(e.status ?? ''),
        createdAt: String(e.created_at ?? ''),
        game: String(e.game ?? ''),
        item: String(e.item ?? ''),
        gameId: String(e.gameid ?? ''),
        zoneId: String(e.zoneid ?? ''),
        amount: String(e.amount ?? ''),
        quantity: e.quantity == null ? undefined : Number(e.quantity),
        fields:
          typeof e.fields === 'object' && e.fields && !Array.isArray(e.fields)
            ? Object.fromEntries(
                Object.entries(e.fields as Record<string, unknown>).map(([key, value]) => [key, String(value ?? '')]),
              )
            : null,
        providerOrderId: e.provider_order_id == null ? null : String(e.provider_order_id),
        playerName: e.player_name == null ? null : String(e.player_name),
        region: e.region == null ? null : String(e.region),
        productCode: e.product_code == null ? null : String(e.product_code),
        deliveryItems: Array.isArray(e.delivery_items) ? e.delivery_items.map(String) : [],
        message: e.message == null ? undefined : String(e.message),
      }));
    });
  }

  async fetchMlInfo(userId: string, gameId: string): Promise<MlInfo> {
    return this.safe(async () => {
      const res = await this.http.post('/api/mlinfo', { userid: userId, gameid: gameId }, { headers: this.headers() });
      const data = res.data as Record<string, unknown>;
      return {
        nickname: String(data.nickname ?? ''),
        region: String(data.region ?? ''),
        lastLogin: String(data.lastLogin ?? ''),
        found: data.found === true,
      };
    });
  }

  async fetchMlDiamondBonus(userId: string, zoneId: string): Promise<MlDiamondBonus | null> {
    return this.safe(async () => {
      // This endpoint should proxy to https://meyangameshop.com/checkml
      // Backend must implement: POST /api/ml-diamond-bonus -> forward to meyangameshop.com/checkml
      const res = await this.http.post('/api/ml-diamond-bonus', { id: userId, zone: zoneId }, {
        headers: this.headers(),
      });
      const data = res.data as Record<string, unknown>;
      const dd = (data.dd ?? {}) as Record<string, boolean>;
      return {
        '50': dd['50'] === true,
        '150': dd['150'] === true,
        '250': dd['250'] === true,
        '500': dd['500'] === true,
      };
    });
  }

  async changePassword(email: string, password: string, newPassword: string): Promise<string> {
    return this.safe(async () => {
      const res = await this.http.post(
        '/api/auth/changePassword',
        { email, password, newpassword: newPassword },
        { headers: this.headers() },
      );
      return String((res.data as { message?: unknown }).message ?? 'Password changed');
    });
  }

  async deleteAccount(): Promise<string> {
    return this.safe(async () => {
      const res = await this.http.delete('/api/auth/me', { headers: this.headers(true) });
      return String((res.data as { message?: unknown }).message ?? 'Account deleted');
    });
  }

  async updateProfile(name?: string, email?: string): Promise<UserProfile> {
    return this.safe(async () => {
      const payload: Record<string, string> = {};
      if (name?.trim()) payload.name = name.trim();
      if (email?.trim()) payload.email = email.trim();
      const res = await this.http.put('/api/auth/me', payload, { headers: this.headers(true) });
      const user = ((res.data as { user?: Record<string, unknown> }).user ?? {}) as Record<string, unknown>;
      return {
        name: String(user.name ?? ''),
        coins: 0,
        email: user.email == null ? null : String(user.email),
      };
    });
  }

  async fetchSupportLink(): Promise<string | null> {
    return this.safe(async () => {
      const res = await this.http.get('/support');
      return (res.data as { telegram?: string }).telegram ?? null;
    });
  }

  async fetchMaintenanceStatus(): Promise<MaintenanceStatus> {
    return this.safe(async () => {
      const res = await this.http.get('/api/maintenance');
      const data = res.data as Record<string, unknown>;
      return {
        enabled: data.enabled === true,
        message: String(data.message ?? ''),
      };
    });
  }

  async fetchUserMaintenance(): Promise<MaintenanceStatus> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user/maintenance');
      const data = res.data as Record<string, unknown>;
      return {
        enabled: data.enabled === true,
        message: String(data.message ?? ''),
      };
    });
  }

  async fetchNotifications(): Promise<UserNotification[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user/notifications', { headers: this.headers(true) });
      const notifications = Array.isArray(res.data) ? res.data : [];
      return notifications.map((e: Record<string, unknown>) => ({
        id: Number(e.id ?? 0),
        title: String(e.title ?? ''),
        message: String(e.message ?? ''),
        url: String(e.url ?? ''),
        createdAt: String(e.created_at ?? ''),
        isRead: e.is_read === 1 || e.is_read === true,
        readAt: e.read_at == null ? null : String(e.read_at),
      }));
    });
  }

  async markAllNotificationsRead(): Promise<{ message: string; updated: number }> {
    return this.safe(async () => {
      const res = await this.http.post('/api/user/notifications/read-all', {}, { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      return {
        message: String(data.message ?? ''),
        updated: Number(data.updated ?? 0),
      };
    });
  }

  async fetchSpinInfo(): Promise<SpinInfo> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user/spin', { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      return {
        availableSpins: Number(data.available_spins ?? 0),
        thresholdMmk: Number(data.threshold_mmk ?? 10000),
        rewards: Array.isArray(data.rewards)
          ? data.rewards.map((r: Record<string, unknown>) => ({
              key: String(r.key ?? ''),
              type: r.type === 'spins' ? 'spins' : 'coins',
              value: Number(r.value ?? 0),
              label: String(r.label ?? ''),
            }))
          : [],
      };
    });
  }

  async doSpin(): Promise<SpinResult> {
    return this.safe(async () => {
      const res = await this.http.post('/api/user/spin', {}, { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      const reward = (data.reward ?? {}) as Record<string, unknown>;
      const rewardType = reward.type === 'spins' ? 'spins' : reward.type === 'none' ? 'none' : 'coins';
      return {
        message: String(data.message ?? 'Spin completed'),
        reward: {
          key: String(reward.key ?? ''),
          type: rewardType,
          value: Number(reward.value ?? 0),
          label: String(reward.label ?? ''),
        },
        availableSpins: Number(data.available_spins ?? 0),
        coins: Number(data.coins ?? 0),
      };
    });
  }

  async fetchDevices(): Promise<DeviceInfo[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/auth/devices', { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      const devices = Array.isArray(data.devices) ? data.devices : [];
      return devices.map((d: Record<string, unknown>) => ({
        id: Number(d.id ?? 0),
        deviceId: String(d.device_id ?? ''),
        deviceName: String(d.device_name ?? 'Unknown Device'),
        createdAt: String(d.created_at ?? ''),
        lastSeenAt: String(d.last_seen_at ?? ''),
      }));
    });
  }

  async removeDevice(deviceId: number): Promise<string> {
    return this.safe(async () => {
      const res = await this.http.delete(`/api/auth/devices/${deviceId}`, { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      return String(data.message ?? 'Device removed');
    });
  }

  async fetchLeaderboard(): Promise<LeaderboardEntry[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user/leaderboard', { headers: this.headers(true) });
      const data = res.data as Record<string, unknown>;
      const leaderboard = Array.isArray(data.leaderboard) ? data.leaderboard : [];
      return leaderboard.map((entry: Record<string, unknown>) => ({
        rank: Number(entry.rank ?? 0),
        name: String(entry.name ?? ''),
        amount: Number(entry.amount ?? 0),
      }));
    });
  }

  // Pack/Provider API methods
  async fetchPackProviders(): Promise<PackProvider[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/packs');
      if (!Array.isArray(res.data)) return [];
      return res.data as PackProvider[];
    });
  }

  async fetchEvent(): Promise<EventData | null> {
    return this.safe(async () => {
      const res = await this.http.get('/api/event');
      const data = res.data as EventData;
      if (!data.is_active) return null;
      return data;
    });
  }

  async fetchUserGuide(): Promise<EventData | null> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user-guide');
      const data = (typeof res.data === 'object' && res.data ? res.data : {}) as Record<string, unknown>;
      const posts = Array.isArray(data.posts) ? data.posts : [];
      return {
        name: String(data.name ?? 'User Guide'),
        image_url: String(data.image_url ?? ''),
        is_active: data.is_active !== false,
        updated_at: String(data.updated_at ?? ''),
        posts: posts.map((post: Record<string, unknown>) => ({
          id: Number(post.id ?? 0),
          title: post.title == null ? undefined : String(post.title),
          message: String(post.message ?? ''),
          images: Array.isArray(post.images) ? post.images.map(String) : [],
          sort: Number(post.sort ?? 0),
          is_active: post.is_active !== false,
          created_at: String(post.created_at ?? ''),
          updated_at: String(post.updated_at ?? ''),
        })),
      };
    });
  }

  async fetchUserGuidePosts(): Promise<UserGuidePostSummary[]> {
    return this.safe(async () => {
      const res = await this.http.get('/api/user-guide/posts');
      const posts = Array.isArray(res.data?.posts) ? res.data.posts : [];
      return posts
        .map((post: Record<string, unknown>) => ({
          id: Number(post.id ?? 0),
          title: String(post.title ?? ''),
          coverPhoto: String(post.cover_photo ?? ''),
          coverMessage: String(post.cover_message ?? ''),
          sort: Number(post.sort ?? 0),
          updatedAt: String(post.updated_at ?? ''),
        }))
        .filter((post: UserGuidePostSummary) => post.id && post.title)
        .sort((a: UserGuidePostSummary, b: UserGuidePostSummary) => a.sort - b.sort);
    });
  }

  async fetchUserGuidePost(postId: number): Promise<UserGuidePostDetail | null> {
    return this.safe(async () => {
      const res = await this.http.get(`/api/user-guide/posts/${encodeURIComponent(String(postId))}`);
      const post = (typeof res.data?.post === 'object' && res.data.post ? res.data.post : null) as Record<string, unknown> | null;
      if (!post) return null;
      const blocks = Array.isArray(post.blocks) ? post.blocks : [];
      return {
        id: Number(post.id ?? 0),
        title: String(post.title ?? ''),
        message: String(post.message ?? ''),
        coverPhoto: String(post.cover_photo ?? ''),
        coverMessage: String(post.cover_message ?? ''),
        blocks: blocks.map((block: Record<string, unknown>) => ({
          photo: String(block.photo ?? ''),
          message: String(block.message ?? ''),
        })),
        sort: Number(post.sort ?? 0),
        isActive: post.is_active !== false,
      };
    });
  }

  async fetchPackProvider(providerId: string): Promise<PackProvider | null> {
    return this.safe(async () => {
      const res = await this.http.get(`/api/packs/${encodeURIComponent(providerId)}`);
      return res.data as PackProvider;
    });
  }

  async fetchOoredooCatalog(phone: string): Promise<{ parents: OoredooParentCategory[] }> {
    return this.safe(async () => {
      const normalizedPhone = normalizeOoredooPhone(phone);
      const params = new URLSearchParams({ phone: normalizedPhone, _: String(Date.now()) });
      const res = await this.http.get(`/api/ooredoo/catalog?${params.toString()}`);
      const data = (res.data || {}) as { parents?: OoredooParentCategory[] };
      return {
        parents: Array.isArray(data.parents) ? data.parents : [],
      };
    });
  }

  async fetchOoredooCollections(phone: string): Promise<{ title: string; itemid: string; items: OoredooCollectionItem[] }> {
    return this.safe(async () => {
      const normalizedPhone = normalizeOoredooPhone(phone);
      const params = new URLSearchParams({ phone: normalizedPhone, _: String(Date.now()) });
      const res = await this.http.get(`/api/ooredoo/collections?${params.toString()}`);
      const data = (res.data || {}) as { title?: string; itemid?: string | number | null; items?: OoredooCollectionItem[] };
      return {
        title: String(data.title || 'BuyPacksOffersList'),
        itemid: String(data.itemid ?? ''),
        items: Array.isArray(data.items) ? data.items : [],
      };
    });
  }

  async fetchOoredooCollectionPackages(phone: string, item: OoredooCollectionItem): Promise<{ categories: PackCategory[]; count: number }> {
    return this.safe(async () => {
      const normalizedPhone = normalizeOoredooPhone(phone);
      const params = new URLSearchParams({
        phone: normalizedPhone,
        itemid: item.itemid,
        uniqueid: item.uniqueid || '',
        _: String(Date.now()),
      });
      const res = await this.http.get(`/api/ooredoo/collection?${params.toString()}`);
      const data = (res.data || {}) as { categories?: PackCategory[]; count?: number };
      return {
        categories: Array.isArray(data.categories) ? data.categories : [],
        count: Number(data.count || 0),
      };
    });
  }

  async fetchOoredooParentCatalog(phone: string, parent: OoredooParentCategory): Promise<{ categories: PackCategory[]; count: number }> {
    return this.safe(async () => {
      const normalizedPhone = normalizeOoredooPhone(phone);
      const params = new URLSearchParams({
        phone: normalizedPhone,
        uniqueid: parent.uniqueid,
        _: String(Date.now()),
      });
      if (parent.itemid) params.set('itemid', parent.itemid);
      const res = await this.http.get(`/api/ooredoo/catalog?${params.toString()}`);
      const data = (res.data || {}) as { categories?: PackCategory[]; count?: number };
      return {
        categories: Array.isArray(data.categories) ? data.categories : [],
        count: Number(data.count || 0),
      };
    });
  }

  async createPackOrder(order: PackOrderRequest): Promise<PackOrder> {
    return this.safe(async () => {
      // Provider failures (especially U9 gift failures) are returned with a
      // provider status such as 502. Accept the response here so the actual
      // Burmese provider message reaches the purchase screen instead of
      // being converted into the generic network error.
      const res = await this.http.post('/api/packs/orders', order, {
        headers: this.headers(true),
        validateStatus: () => true,
      });
      if (res.status < 200 || res.status >= 300) {
        throw new ApiError(this.messageFromBody(res.data));
      }
      const data = res.data as PackOrder & { status?: string; message?: string; ok?: boolean; success?: boolean };
      const orderStatus = String(data?.status || '').toLowerCase();
      if (['rejected', 'failed', 'failure'].includes(orderStatus) || data?.ok === false || data?.success === false) {
        throw new ApiError(this.messageFromBody(data));
      }
      return data;
    });
  }

}

export const apiClient = new ApiClient();
